import React from 'react';
import { motion } from 'framer-motion';
import { MoreHorizontalIcon, ChevronRightIcon, ClockIcon, MapPinIcon, CalendarDaysIcon, TriangleAlertIcon, ArrowRightIcon, HeartIcon, CheckIcon, ClipboardCheckIcon, UsersIcon, PartyPopperIcon, ShieldCheckIcon, ZapIcon, SearchIcon, SparklesIcon, ShieldIcon, SettingsIcon, CircleCheckIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
type Bilingual = {
  en: string;
  fr: string;
};
const EASE = [0.23, 1, 0.32, 1] as const;
const PHOTO_AQUATIC = 'https://cdn.magicpatterns.com/patterns/generated-images/63449ba5-49b8-4653-86cc-cfc1450645e0.jpg';
const PHOTO_SWIMMER = 'https://cdn.magicpatterns.com/patterns/generated-images/b78882f6-12ee-4544-b668-5f9e6d917737.jpg';
const PHOTO_MULTISPORT = 'https://cdn.magicpatterns.com/patterns/generated-images/f681b547-5d46-4886-a2f2-a68d4818b7d7.jpg';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  }
};
const panel = 'rounded-2xl border border-[#ECEEF2] bg-white shadow-[0_10px_30px_rgba(18,18,40,0.06)]';
const SWIM_RESULTS: {
  photo: string;
  title: Bilingual;
  age: Bilingual;
  time: Bilingual;
  place: Bilingual;
}[] = [{
  photo: PHOTO_AQUATIC,
  title: {
    fr: 'Initiation à la natation',
    en: 'Intro to swimming'
  },
  age: {
    fr: '5 à 8 ans',
    en: 'Ages 5 to 8'
  },
  time: {
    fr: 'Sam. 9 h – 10 h',
    en: 'Sat 9–10 AM'
  },
  place: {
    fr: 'Complexe aquatique',
    en: 'Aquatic complex'
  }
}, {
  photo: PHOTO_SWIMMER,
  title: {
    fr: 'Natation avancée',
    en: 'Advanced swimming'
  },
  age: {
    fr: '8 à 12 ans',
    en: 'Ages 8 to 12'
  },
  time: {
    fr: 'Sam. 10 h – 11 h',
    en: 'Sat 10–11 AM'
  },
  place: {
    fr: 'Complexe aquatique',
    en: 'Aquatic complex'
  }
}, {
  photo: PHOTO_AQUATIC,
  title: {
    fr: 'Aqua-jeux',
    en: 'Aqua games'
  },
  age: {
    fr: '5 à 10 ans',
    en: 'Ages 5 to 10'
  },
  time: {
    fr: 'Sam. 9 h – 10 h',
    en: 'Sat 9–10 AM'
  },
  place: {
    fr: 'Piscine municipale',
    en: 'Municipal pool'
  }
}];
const FLOW: {
  icon: typeof SearchIcon;
  title: Bilingual;
  desc: Bilingual;
}[] = [{
  icon: SearchIcon,
  title: {
    fr: 'Comprend',
    en: 'Understands'
  },
  desc: {
    fr: 'Qui, quoi, quand, où et pourquoi',
    en: 'Who, what, when, where and why'
  }
}, {
  icon: UsersIcon,
  title: {
    fr: 'Contextualise',
    en: 'Contextualizes'
  },
  desc: {
    fr: 'Utilise le contexte autorisé',
    en: 'Uses authorized context'
  }
}, {
  icon: SparklesIcon,
  title: {
    fr: 'Recommande',
    en: 'Recommends'
  },
  desc: {
    fr: 'Les meilleures options pour vous',
    en: 'The best options for you'
  }
}, {
  icon: CalendarDaysIcon,
  title: {
    fr: 'Anticipe',
    en: 'Anticipates'
  },
  desc: {
    fr: 'Détecte les préalables et les échéances',
    en: 'Detects prerequisites and deadlines'
  }
}, {
  icon: ShieldIcon,
  title: {
    fr: 'Vérifie',
    en: 'Verifies'
  },
  desc: {
    fr: 'Valide l’admissibilité et la disponibilité',
    en: 'Validates eligibility and availability'
  }
}, {
  icon: SettingsIcon,
  title: {
    fr: 'Orchestre',
    en: 'Orchestrates'
  },
  desc: {
    fr: 'Connecte les systèmes et les ressources',
    en: 'Connects systems and resources'
  }
}, {
  icon: CircleCheckIcon,
  title: {
    fr: 'Agit',
    en: 'Acts'
  },
  desc: {
    fr: 'Vous accompagne jusqu’à la participation',
    en: 'Guides you through to participation'
  }
}];
function StepHeader({
  num,
  title,
  desc
}: {
  num: string;
  title: string;
  desc: string;
}) {
  return <div className="flex items-start gap-4" data-id="element-4283" data-mp="3urjgfc9a5wfp">
      <span className="shrink-0 w-11 h-11 rounded-xl bg-[#EEEDFD] flex items-center justify-center font-sans text-lg font-semibold text-[#4F46E5]" data-id="element-4284" data-mp="3usb13s1ca9r7">
        {num}
      </span>
      <div data-id="element-4285" data-mp="7fqw3yyvh6zb">
        <h3 className="font-sans text-[17px] font-semibold tracking-[0.06em] uppercase text-[#121212]" data-id="element-4286" data-mp="7fiig4uxdxn2">{title}</h3>
        <p className="font-sans text-[15px] text-[#5B6478] leading-snug mt-1" data-id="element-4287" data-mp="2mpdpgzgipaz">{desc}</p>
      </div>
    </div>;
}
function Caption({
  icon: Icon,
  children
}: {
  icon: typeof UsersIcon;
  children: React.ReactNode;
}) {
  return <p className="flex items-center gap-3 rounded-2xl bg-[#F4F4FB] px-5 py-3.5 font-sans text-[14px] text-[#121212]" data-id="element-4288" data-mp="370e0kx3cb45e">
      <Icon className="w-[18px] h-[18px] shrink-0 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-4289" data-mp="3oq9ejklvcqz9" />
      {children}
    </p>;
}
export function LoisirsJourney() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  return <div className="max-w-[1480px] mx-auto" data-id="element-4290" data-mp="7ab15pjyzzia">
      {/* Intro */}
      <motion.div {...reveal} transition={{
      duration: 0.5,
      ease: EASE
    }} className="max-w-4xl" data-id="element-4291" data-mp="7c400iocdffq">
        <p className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#5B6478]" data-id="element-4292" data-mp="bdpjwnzamtzf">
          {isFR ? 'Plus qu’un catalogue d’activités' : 'More than an activity catalogue'}
        </p>
        <h2 className="font-sans font-semibold text-4xl md:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-[-0.03em] text-[#121212] mt-4" data-id="element-4293" data-mp="l7pwfawwn1i6">
          ATNA <span className="text-[#4F46E5]" data-id="element-4294" data-mp="2n0dtt3mvauq1">{isFR ? 'comprend le parcours.' : 'understands the journey.'}</span>
        </h2>
        <p className="font-sans text-lg text-[#5B6478] leading-relaxed mt-5 max-w-3xl" data-id="element-4295" data-mp="2fifyc31itk5t">
          {isFR ? 'De la première recherche jusqu’à la participation, ATNA utilise le contexte autorisé pour recommander, anticiper les préalables et accompagner le citoyen jusqu’à l’action.' : 'From the first search to participation, ATNA uses authorized context to recommend, anticipate prerequisites and guide people all the way to action.'}
        </p>
      </motion.div>

      {/* Three experiences */}
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-5 lg:gap-3 items-stretch" data-id="element-4296" data-mp="2ti7arzce2shp">
        {/* 01 — DISCOVER */}
        <motion.div {...reveal} transition={{
        duration: 0.5,
        ease: EASE
      }} className="flex flex-col gap-4 min-w-0" data-id="element-4297" data-mp="2jjrezj2f2v1v">
          <div className="flex-1 rounded-3xl border border-[#EEF0F4] bg-[#FCFCFE] p-5 lg:p-6 flex flex-col gap-6" data-id="element-4298" data-mp="1d48nyk2gu4oy">
            <StepHeader num="01" title={isFR ? 'Découvre' : 'Discovers'} desc={isFR ? 'ATNA trouve les activités qui vous conviennent.' : 'ATNA finds the activities that suit you.'} data-id="element-4299" data-mp="183b4m45pz4lv" />
            <div className={`${panel} p-4 flex-1`} aria-hidden="true" data-id="element-4300" data-mp="43wf4q6qmg75">
              <div className="flex items-center gap-2.5 pb-3 border-b border-[#F0F1F5]" data-id="element-4301" data-mp="3mblvakiszpuj">
                <span className="w-7 h-7 rounded-full border-[3px] border-[#4F46E5]" data-id="element-4302" data-mp="3r9dlna3sv3wm" />
                <span className="flex-1" data-id="element-4303" data-mp="3ckzj70aaphgl">
                  <span className="block font-sans text-[13px] font-semibold text-[#121212] leading-tight" data-id="element-4304" data-mp="3ck7pjv09dtsy">ATNA</span>
                  <span className="block font-sans text-[10.5px] text-[#5B6478]" data-id="element-4305" data-mp="33lm9q7m68svv">
                    {isFR ? 'Assistant loisirs' : 'Recreation assistant'}
                  </span>
                </span>
                <MoreHorizontalIcon className="w-4 h-4 text-[#121212]" data-id="element-4306" data-mp="2tngc427ee8wt" />
              </div>
              <div className="ml-auto mt-3 max-w-[82%] rounded-2xl rounded-br-md bg-[#EEF0FE] px-3.5 py-2.5" data-id="element-4307" data-mp="2ym9lbifnjdr6">
                <p className="font-sans text-[12.5px] text-[#3730A3] leading-snug" data-id="element-4308" data-mp="2ezy16m38si1y">
                  {isFR ? 'Une activité de natation pour Léa le samedi matin.' : 'A swimming activity for Léa on Saturday morning.'}
                </p>
              </div>
              <div className="mt-2.5 max-w-[88%] rounded-2xl rounded-bl-md bg-[#F5F6F8] px-3.5 py-2.5" data-id="element-4309" data-mp="2ozylobcul47q">
                <p className="font-sans text-[12.5px] text-[#121212] leading-snug" data-id="element-4310" data-mp="2k1ngidxhzhvu">
                  {isFR ? 'J’ai trouvé 3 activités qui correspondent.' : 'I found 3 matching activities.'}
                </p>
              </div>
              <ul className="mt-3 space-y-2" data-id="element-4311" data-mp="37oviomzmqatn">
                {SWIM_RESULTS.map(r => <li key={r.title.en} className="flex items-center gap-3 rounded-xl border border-[#F0F1F5] p-1.5 pr-2.5" data-id="element-4312" data-mp="32q27x2bq8xx6">
                    <img src={r.photo} alt="" className="w-16 h-14 rounded-lg object-cover shrink-0" data-id="element-4313" data-mp="3hmjgxmwg20gk" />
                    <span className="flex-1 min-w-0" data-id="element-4314" data-mp="3cp16tt6oxf6z">
                      <span className="flex items-center justify-between gap-2" data-id="element-4315" data-mp="3cmeyhrbz6vnp">
                        <span className="font-sans text-[12px] font-semibold text-[#121212] truncate" data-id="element-4316" data-mp="1xbnps3m2a7xm">{pick(r.title)}</span>
                        <span className="shrink-0 rounded-full bg-[#ECFDF3] px-2 py-0.5 font-sans text-[9.5px] font-medium text-[#15803D]" data-id="element-4317" data-mp="27a3fc25fx950">
                          {isFR ? 'Places disponibles' : 'Spots open'}
                        </span>
                      </span>
                      <span className="block font-sans text-[10.5px] text-[#5B6478]" data-id="element-4318" data-mp="1en8ydf7u0rwa">{pick(r.age)}</span>
                      <span className="flex flex-wrap gap-x-3 font-sans text-[10.5px] text-[#5B6478] mt-0.5" data-id="element-4319" data-mp="1jksekko3beqv">
                        <span className="flex items-center gap-1" data-id="element-4320" data-mp="1ohr38nozitho"><ClockIcon className="w-3 h-3" data-id="element-4321" data-mp="1tak7zbkge1tr" />{pick(r.time)}</span>
                        <span className="flex items-center gap-1" data-id="element-4322" data-mp="vv6c9ocf8jen"><MapPinIcon className="w-3 h-3" data-id="element-4323" data-mp="vu52rob204z1" />{pick(r.place)}</span>
                      </span>
                    </span>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-[#9AA0AE] shrink-0" data-id="element-4324" data-mp="10rwuoibp7r38" />
                  </li>)}
              </ul>
            </div>
          </div>
          <Caption icon={UsersIcon} data-id="element-4325" data-mp="3a37c6ewmytgk">
            {isFR ? 'Comprend la personne · le moment · le besoin' : 'Understands the person · the moment · the need'}
          </Caption>
        </motion.div>

        <StepArrow data-id="element-4326" data-mp="303zu5eeyian4" />

        {/* 02 — ANTICIPATE */}
        <motion.div {...reveal} transition={{
        duration: 0.5,
        delay: 0.08,
        ease: EASE
      }} className="flex flex-col gap-4 min-w-0" data-id="element-4327" data-mp="2v5ydt18x0pfc">
          <div className="flex-1 rounded-3xl border border-[#EEF0F4] bg-[#FCFCFE] p-5 lg:p-6 flex flex-col gap-6" data-id="element-4328" data-mp="2qdwuivdbxyft">
            <StepHeader num="02" title={isFR ? 'Anticipe' : 'Anticipates'} desc={isFR ? 'ATNA vérifie les préalables avant qu’ils deviennent un problème.' : 'ATNA checks prerequisites before they become a problem.'} data-id="element-4329" data-mp="2mdmrnrku3o56" />
            <div className="flex-1 flex flex-col justify-center gap-4" aria-hidden="true" data-id="element-4330" data-mp="1w4yslnckiwda">
              <div className={`${panel} p-4 shadow-[0_18px_40px_rgba(79,70,229,0.10)]`} data-id="element-4331" data-mp="1r65epttdd3cl">
                <div className="flex items-center gap-3" data-id="element-4332" data-mp="1r4b1z2s94hjl">
                  <span className="w-11 h-11 rounded-xl bg-[#EEEDFD] flex items-center justify-center shrink-0" data-id="element-4333" data-mp="24w8v445ki2u4">
                    <CalendarDaysIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-4334" data-mp="20wgr84r4ckv3" />
                  </span>
                  <span className="flex-1" data-id="element-4335" data-mp="1d8gu8n37ry82">
                    <span className="block font-sans text-[15px] font-semibold text-[#121212]" data-id="element-4336" data-mp="1881h9wmugsqm">
                      {isFR ? 'Inscriptions dans 3 jours' : 'Registration in 3 days'}
                    </span>
                    <span className="block font-sans text-[12px] text-[#5B6478]" data-id="element-4337" data-mp="1h6wmp6i2dysv">
                      {isFR ? 'Camp multisport – Été 2026' : 'Multisport camp – Summer 2026'}
                    </span>
                  </span>
                  <ChevronRightIcon className="w-4 h-4 text-[#9AA0AE]" data-id="element-4338" data-mp="2smv1lqzxbf20" />
                </div>
                <motion.div initial={{
                opacity: 0,
                scale: 0.96
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true,
                margin: '-80px'
              }} transition={{
                duration: 0.3,
                delay: 0.45,
                ease: EASE
              }} className="mt-4 rounded-xl border border-[#F5E3B8] bg-[#FFF9EC] p-4" data-id="element-4339" data-mp="312kbq4ajy6ky">
                  <p className="flex gap-3" data-id="element-4340" data-mp="3jvo9o1usz5ld">
                    <TriangleAlertIcon className="w-6 h-6 shrink-0 text-[#E3A008]" strokeWidth={2} data-id="element-4341" data-mp="3jwzbvx8lo5ad" />
                    <span data-id="element-4342" data-mp="3ovj064ao1t5w">
                      <span className="block font-sans text-[15px] font-semibold text-[#9A4A0B] leading-snug" data-id="element-4343" data-mp="3a5bpiaqwhefr">
                        {isFR ? 'La carte numérique BCITI de Léa expire demain.' : 'Léa’s BCITI Digital Card expires tomorrow.'}
                      </span>
                      <span className="block font-sans text-[12px] text-[#6B5B3E] leading-snug mt-1.5" data-id="element-4344" data-mp="6m9z26201kae">
                        {isFR ? 'Elle doit être valide pour s’inscrire aux activités et camps de la ville.' : 'It must be valid to register for city activities and camps.'}
                      </span>
                      <span className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#4F46E5] px-4 py-2 font-sans text-[12.5px] font-medium text-white" data-id="element-4345" data-mp="bbazx6viy5tc">
                        {isFR ? 'Renouveler maintenant' : 'Renew now'}
                        <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-4346" data-mp="2e0rdta3cus4" />
                      </span>
                    </span>
                  </p>
                </motion.div>
              </div>
              <div className={`${panel} mx-3 p-2 flex items-center gap-3 opacity-90`} data-id="element-4347" data-mp="3ors7g7e33y4m">
                <div className="relative shrink-0" data-id="element-4348" data-mp="3ju0jfogk8w5p">
                  <img src={PHOTO_MULTISPORT} alt="" className="w-24 h-[72px] rounded-lg object-cover" data-id="element-4349" data-mp="3fqkk2kd3whpb" />
                  <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white flex items-center justify-center" data-id="element-4350" data-mp="3ar7qlgrqccdr">
                    <HeartIcon className="w-3 h-3 text-[#EF4444] fill-[#EF4444]" data-id="element-4351" data-mp="1q8x7tpa5cocl" />
                  </span>
                </div>
                <span className="min-w-0" data-id="element-4352" data-mp="1vawbl09stna2">
                  <span className="block font-sans text-[12.5px] font-semibold text-[#121212]" data-id="element-4353" data-mp="1z9v9i0x44js0">
                    {isFR ? 'Camp multisport' : 'Multisport camp'}
                  </span>
                  <span className="block font-sans text-[10.5px] text-[#5B6478]" data-id="element-4354" data-mp="17jh4yvazojt0">{isFR ? '5 à 12 ans' : 'Ages 5 to 12'}</span>
                  <span className="flex items-center gap-1 font-sans text-[10.5px] text-[#5B6478] mt-1" data-id="element-4355" data-mp="1blu7zbycq3m3">
                    <ClockIcon className="w-3 h-3" data-id="element-4356" data-mp="1gj3uojucw168" />
                    {isFR ? 'Du 6 juillet au 14 août 2026' : 'July 6 to Aug 14, 2026'}
                  </span>
                  <span className="flex items-center gap-1 font-sans text-[10.5px] text-[#5B6478]" data-id="element-4357" data-mp="nvhqpah7uuiu">
                    <MapPinIcon className="w-3 h-3" data-id="element-4358" data-mp="su1hbo6r8uhj" />
                    École Saint-Gabriel
                  </span>
                </span>
              </div>
            </div>
          </div>
          <Caption icon={ShieldCheckIcon} data-id="element-4359" data-mp="2qhvfvp7w4b03">
            {isFR ? 'Vérifie les préalables avant qu’ils deviennent un problème.' : 'Checks prerequisites before they become a problem.'}
          </Caption>
        </motion.div>

        <StepArrow data-id="element-4360" data-mp="2zibiyxkq1g4f" />

        {/* 03 — ACCOMPANY */}
        <motion.div {...reveal} transition={{
        duration: 0.5,
        delay: 0.16,
        ease: EASE
      }} className="flex flex-col gap-4 min-w-0" data-id="element-4361" data-mp="2zggy1j9g9o0c">
          <div className="flex-1 rounded-3xl border border-[#EEF0F4] bg-[#FCFCFE] p-5 lg:p-6 flex flex-col gap-6" data-id="element-4362" data-mp="398mxfe2177pk">
            <StepHeader num="03" title={isFR ? 'Accompagne' : 'Guides'} desc={isFR ? 'ATNA vous guide jusqu’à l’inscription.' : 'ATNA guides you through to registration.'} data-id="element-4363" data-mp="3n8fojahgzpy2" />
            <div className={`${panel} p-5 flex-1 flex flex-col`} aria-hidden="true" data-id="element-4364" data-mp="1sun1sfa7y484">
              <ul className="space-y-4" data-id="element-4365" data-mp="1otusxn59e1cd">
                <li className="flex items-center gap-3" data-id="element-4366" data-mp="1ouw3lqid4ltk">
                  <Check data-id="element-4367" data-mp="1epw7rlysmqed" />
                  <span className="shrink-0 w-[68px] h-11 rounded-lg bg-[#4F46E5] p-1.5 flex flex-col justify-between" data-id="element-4368" data-mp="1eqphmvt1tw0q">
                    <span className="flex items-center justify-between" data-id="element-4369" data-mp="14yt72l9s0dfm">
                      <span className="font-sans text-[6px] font-bold text-white tracking-wide" data-id="element-4370" data-mp="14yjgowktk49e">BCITI</span>
                      <span className="w-3 h-3 rounded-full bg-white/80" data-id="element-4371" data-mp="19vry2uy5849l" />
                    </span>
                    <span className="block h-1 w-8 rounded bg-white/60" data-id="element-4372" data-mp="v2q2l8rgc2pi" />
                  </span>
                  <span className="min-w-0" data-id="element-4373" data-mp="zxlyijzt7a7w">
                    <span className="block font-sans text-[13px] font-semibold text-[#121212]" data-id="element-4374" data-mp="m0pb6siwgavr">
                      {isFR ? 'Carte numérique renouvelée' : 'Digital Card renewed'}
                    </span>
                    <span className="block font-sans text-[11.5px] text-[#5B6478]" data-id="element-4375" data-mp="2gezygwjuiwzf">
                      {isFR ? 'Valide jusqu’au 31 déc. 2028' : 'Valid until Dec 31, 2028'}
                    </span>
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-4376" data-mp="2iw1q1jkucle8">
                  <Check data-id="element-4377" data-mp="2evqaumb8zda5" />
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-[#F3F2FE] flex items-center justify-center" data-id="element-4378" data-mp="2evhu2jc07nto">
                    <ClipboardCheckIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-4379" data-mp="29zjara5j8w04" />
                  </span>
                  <span className="min-w-0" data-id="element-4380" data-mp="32m42wctu1c4q">
                    <span className="block font-sans text-[13px] font-semibold text-[#121212]" data-id="element-4381" data-mp="32l2hbef6x78r">
                      {isFR ? 'Préalables vérifiés' : 'Prerequisites verified'}
                    </span>
                    <span className="block font-sans text-[11.5px] text-[#5B6478]" data-id="element-4382" data-mp="2suzmd8oidj7s">
                      {isFR ? 'Âge, dossier, formulaire, documents' : 'Age, record, form, documents'}
                    </span>
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-4383" data-mp="3glmxs3xmuqz3">
                  <Check data-id="element-4384" data-mp="3gk22gc3hv406" />
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-[#F3F2FE] flex items-center justify-center" data-id="element-4385" data-mp="3nxb516bqsu3s">
                    <UsersIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-4386" data-mp="3svuo8yyybrsg" />
                  </span>
                  <span className="min-w-0" data-id="element-4387" data-mp="1ptkzcl8a2hd">
                    <span className="block font-sans text-[13px] font-semibold text-[#121212]" data-id="element-4388" data-mp="1s625nj5w8td">
                      {isFR ? 'Place disponible' : 'Spot available'}
                    </span>
                    <span className="block font-sans text-[11.5px] text-[#5B6478]" data-id="element-4389" data-mp="aqhqfiwk2uin">
                      {isFR ? 'Camp multisport – 6 juillet 2026' : 'Multisport camp – July 6, 2026'}
                    </span>
                  </span>
                </li>
              </ul>
              <div className="mt-auto pt-5 border-t border-[#F0F1F5]" data-id="element-4390" data-mp="pemcmwutho66">
                <p className="flex items-center justify-center gap-3 font-sans text-[18px] font-semibold text-[#121212] leading-snug text-center" data-id="element-4391" data-mp="ufaao2hgr59w">
                  <PartyPopperIcon className="w-6 h-6 text-[#4F46E5] shrink-0" strokeWidth={1.7} data-id="element-4392" data-mp="yglshlu3im7j" />
                  {isFR ? 'Tout est prêt pour l’inscription.' : 'Everything is ready for registration.'}
                </p>
                <span className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#4F46E5] py-2.5 font-sans text-[13px] font-medium text-white" data-id="element-4393" data-mp="1fs1p8ix48viu">
                  {isFR ? 'Poursuivre' : 'Continue'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-4394" data-mp="110sw3zu08s0y" />
                </span>
              </div>
            </div>
          </div>
          <Caption icon={ZapIcon} data-id="element-4395" data-mp="w2hm9p3pmhiq">
            {isFR ? 'Le parcours continue sans recommencer à zéro.' : 'The journey continues without starting over.'}
          </Caption>
        </motion.div>
      </div>

      {/* Intelligence flow */}
      <motion.ol {...reveal} transition={{
      duration: 0.5,
      delay: 0.1,
      ease: EASE
    }} className="mt-14 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-8" data-id="element-4396" data-mp="15ykyjns9zwje">
        {FLOW.map(({
        icon: Icon,
        title,
        desc
      }, i) => <li key={title.en} className="relative" data-id="element-4397" data-mp="2oybrwhn705e7" data-mpi={i}>
            <span className="w-12 h-12 rounded-full bg-[#F3F2FE] flex items-center justify-center" data-id="element-4398" data-mp="2owh856nnqjbp">
              <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4399" data-mp="2trdbh2ycgt44" />
            </span>
            {i < FLOW.length - 1 && <ArrowRightIcon className="hidden lg:block absolute top-4 left-[4.25rem] w-4 h-4 text-[#4F46E5]/70" aria-hidden="true" data-id="element-4400" data-mp="2k0io1v9d3nh3" />}
            <p className="font-sans text-[15px] font-semibold text-[#121212] mt-3" data-id="element-4401" data-mp="3coejyxr3xbct">{pick(title)}</p>
            <p className="font-sans text-[13px] text-[#5B6478] leading-snug mt-1" data-id="element-4402" data-mp="2yp6exo89j90p">{pick(desc)}</p>
          </li>)}
      </motion.ol>
    </div>;
}
function StepArrow() {
  return <div className="flex items-center justify-center lg:pb-16" aria-hidden="true" data-id="element-4403" data-mp="1n8otej67eqsu">
      <ChevronRightIcon className="hidden lg:block w-6 h-6 text-[#4F46E5]" strokeWidth={2} data-id="element-4404" data-mp="1s81g93x2rxvf" />
      <ChevronRightIcon className="lg:hidden w-5 h-5 rotate-90 text-[#4F46E5]" strokeWidth={2} data-id="element-4405" data-mp="22hp1lr0819e9" />
    </div>;
}
function Check() {
  return <span className="shrink-0 w-6 h-6 rounded-full bg-[#22A55B] flex items-center justify-center" data-id="element-4406" data-mp="27fqhk32cosli">
      <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-4407" data-mp="1sipre3zt55p5" />
    </span>;
}